import cn from 'classnames';
import React, { useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import NotFoundErrorImage from '../../assets/image-404.svg';
import CommonErrorImage from '../../assets/image-error.svg';
import SuccessImage from '../../assets/image-success.svg';
import { DEFAULT_ERROR_MESSAGE, DEFAULT_ERROR_TITLE } from '../../constants/errors';
import { CLAIM_SUCCESS_STATUSES } from '../../constants/http';
import { FOOTER_DISCLAIMER } from '../../constants/ui';
import { usePostClaimCta } from '../../hooks/usePostClaimCta';
import { selectOrgLogo } from '../../store/selectors/derived';
import { selectResultScreenColor } from '../../store/selectors/init';
import { INavigationState } from '../../types/navigation';
import { isLightColor } from '../../util/ui';
import BrandLogo from '../BrandLogo/BrandLogo';
import SubmitButton from '../SubmitButton/SubmitButton';

import styles from './ResultComponent.module.scss';

const ResultComponent = () => {
  const { state, pathname } = useLocation<INavigationState>();

  const backgroundColor = useSelector(selectResultScreenColor);
  const selectOrgLogoMemo = useMemo(() => selectOrgLogo(pathname), [pathname]);
  const orgLogo = useSelector(selectOrgLogoMemo);

  const isBackgroundLight = useMemo(() => {
    return isLightColor(backgroundColor);
  }, [backgroundColor]);

  const message = useMemo(() => (state && state.message ? state.message : DEFAULT_ERROR_MESSAGE), [state]);
  const heading = useMemo(() => (state && state.title ? state.title : DEFAULT_ERROR_TITLE), [state]);
  const status = useMemo(() => (state && state.status ? state.status : 400), [state]);

  const isClaimSuccessful = useMemo(() => CLAIM_SUCCESS_STATUSES.includes(status), [status]);
  const { cta, countdownLabel, redirect } = usePostClaimCta(isClaimSuccessful);

  const { focusImage, footerText } = useMemo(() => {
    const successText = (
      <React.Fragment>
        Visit{' '}
        <a href="https://imprintengine.com" className={styles.footerLink}>
          Imprintengine.com
        </a>{' '}
        for more opportunities!
      </React.Fragment>
    );

    switch (status) {
      case 200:
      case 201:
      case 204:
        return {
          focusImage: SuccessImage,
          footerText: successText,
        };
      case 404:
        return { focusImage: NotFoundErrorImage, footerText: FOOTER_DISCLAIMER };
      default:
        return { focusImage: CommonErrorImage, footerText: FOOTER_DISCLAIMER };
    }
  }, [status]);

  return (
    <div className={styles.container} style={{ backgroundColor }}>
      <Container as="header" className={styles.header}>
        <BrandLogo image={orgLogo || ''} />
      </Container>
      <Container as="main" className={cn(styles.main, { [styles.lightBackground]: isBackgroundLight })}>
        <Row className={styles.content}>
          <Col xs={12} md={6} className={styles.text}>
            <h1 className={styles.title}>{heading}</h1>
            <div className={styles.message} dangerouslySetInnerHTML={{ __html: message }} />
            {cta && (
              <div className={styles.backButtonRow}>
                <SubmitButton onSubmit={redirect}>{`${cta.label} (${countdownLabel})`}</SubmitButton>
              </div>
            )}
          </Col>
          <Col xs={12} md={6} className={styles.focus}>
            <img className={styles.focusImage} src={focusImage} alt="" data-testid="result-focus-image" />
          </Col>
        </Row>
      </Container>
      <Container as="footer" className={cn(styles.footer, { [styles.lightBackground]: isBackgroundLight })}>
        <span className={styles.footerMessage}>{footerText}</span>
        <span className={styles.powered}>
          Powered by{' '}
          <a href="https://imprintengine.com" className={styles.footerLink}>
            Imprint Engine
          </a>
          . All rights reserved
        </span>
      </Container>
    </div>
  );
};

export default ResultComponent;
